import { ChevronLeft, ChevronRight } from "lucide-react";

/**
 * Compact pagination bar.
 *
 * Renders nothing when `total <= perPage` — the caller doesn't need to guard.
 * Shows: "N–M of TOTAL"  ‹ prev · [1] [2] … [k] · next ›
 *
 * Keeps the current page centred in a 5-wide window so long lists stay
 * readable on both desktop and mobile without wrapping.
 */
export default function Pagination({
  page,
  perPage,
  total,
  onPageChange,
  testidPrefix = "pagination",
}) {
  const pageCount = Math.max(1, Math.ceil(total / perPage));
  if (total <= perPage) return null;

  const clamped = Math.min(Math.max(1, page), pageCount);
  const start = (clamped - 1) * perPage + 1;
  const end = Math.min(clamped * perPage, total);

  // Compact 5-wide window centred on current page.
  const win = 2;
  let lo = Math.max(1, clamped - win);
  let hi = Math.min(pageCount, clamped + win);
  if (clamped <= win) hi = Math.min(pageCount, 1 + 2 * win);
  if (clamped > pageCount - win) lo = Math.max(1, pageCount - 2 * win);
  const pages = [];
  for (let i = lo; i <= hi; i++) pages.push(i);

  const go = (p) => onPageChange(Math.min(Math.max(1, p), pageCount));

  return (
    <div
      className="mt-3 flex items-center justify-between gap-3 flex-wrap"
      data-testid={testidPrefix}
    >
      <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono-live" data-testid={`${testidPrefix}-range`}>
        {start}–{end} of {total}
      </div>
      <div className="flex items-center gap-1">
        <button
          onClick={() => go(clamped - 1)}
          disabled={clamped === 1}
          className="flex items-center gap-1 border border-cmdBorder hover:border-white disabled:opacity-30 disabled:hover:border-cmdBorder px-2 py-1 text-[11px] uppercase tracking-widest transition-colors"
          data-testid={`${testidPrefix}-prev`}
          aria-label="Previous page"
        >
          <ChevronLeft className="w-3 h-3" /> Prev
        </button>
        {lo > 1 && (
          <>
            <button
              onClick={() => go(1)}
              className="border border-cmdBorder hover:border-white px-2 py-1 text-[11px] font-mono-live transition-colors"
              data-testid={`${testidPrefix}-page-1`}
            >
              1
            </button>
            {lo > 2 && <span className="text-zinc-600 text-xs px-1">…</span>}
          </>
        )}
        {pages.map((p) => (
          <button
            key={p}
            onClick={() => go(p)}
            className={`border px-2 py-1 text-[11px] font-mono-live transition-colors ${
              p === clamped
                ? "border-white bg-white text-black font-bold"
                : "border-cmdBorder hover:border-white"
            }`}
            data-testid={`${testidPrefix}-page-${p}`}
          >
            {p}
          </button>
        ))}
        {hi < pageCount && (
          <>
            {hi < pageCount - 1 && <span className="text-zinc-600 text-xs px-1">…</span>}
            <button
              onClick={() => go(pageCount)}
              className="border border-cmdBorder hover:border-white px-2 py-1 text-[11px] font-mono-live transition-colors"
              data-testid={`${testidPrefix}-page-${pageCount}`}
            >
              {pageCount}
            </button>
          </>
        )}
        <button
          onClick={() => go(clamped + 1)}
          disabled={clamped === pageCount}
          className="flex items-center gap-1 border border-cmdBorder hover:border-white disabled:opacity-30 disabled:hover:border-cmdBorder px-2 py-1 text-[11px] uppercase tracking-widest transition-colors"
          data-testid={`${testidPrefix}-next`}
          aria-label="Next page"
        >
          Next <ChevronRight className="w-3 h-3" />
        </button>
      </div>
    </div>
  );
}
